import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TelXLSpinnerInputComponent } from '@dxp/elements';
import { durationToSeconds, secondsToDuration } from '@dxp/core/helpers';
import {
    ITargetThreshold,
    IThreshold,
    IKpiValueChange,
} from '@dxp/core/models';
import { Duration } from 'date-fns';

export enum SettingPanelType {
    Percentage = 'Percentage',
    Time = 'Time',
    Numeric = 'Numeric',
}

@Component({
    selector: 'telxl-shared-setting-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, TelXLSpinnerInputComponent],
    templateUrl: './setting-panel.component.html',
    styleUrl: './setting-panel.component.scss',
})
export class TelXLSettingPanelComponent implements OnChanges {
    secondsToDuration = secondsToDuration;
    SettingPanelType = SettingPanelType;
    @Input() icon!: string;
    @Input({ required: true }) heading!: string;
    @Input({ required: true }) settingPanelType!: SettingPanelType;
    @Input({ required: true }) value!: IThreshold | ITargetThreshold;
    @Input() min!: number;
    @Input() max!: number;
    @Input() orientation: 'horizontal' | 'vertical' = 'vertical';
    @Input() compact = false;
    @Input() showErrorIcon = false;
    @Output() valueChange: EventEmitter<IKpiValueChange> =
        new EventEmitter<IKpiValueChange>();

    isEnabled = this.value?.enabled ?? false;
    _value!: IThreshold | ITargetThreshold;
    _lowTime!: Duration;
    _highTime!: Duration;
    _targetTime!: Duration;

    timeFieldChanged = '';
    valueFieldChanged = '';

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes['value'] &&
            changes['value'].currentValue !== undefined &&
            changes['value'].currentValue !== null
        ) {
            this.value = changes['value'].currentValue as
                | IThreshold
                | ITargetThreshold;
            this.isEnabled = this.value.enabled;
            this._value = { ...this.value };
            this._lowTime = secondsToDuration(this.value.low);
            this._highTime = secondsToDuration(this.value.high);

            if (this.isTargetThreshold) {
                this._targetTime = secondsToDuration(
                    (this.value as ITargetThreshold).target,
                );
            }
        }
    }

    onTargetValueChange(newValue: number | null) {
        this.valueFieldChanged = 'T';
        (this._value as ITargetThreshold).target =
            this.settingPanelType === SettingPanelType.Percentage
                ? (newValue ?? 0) / 100
                : (newValue ?? 0);

        this.#emitValueChange();
    }

    onLowValueChange(newValue: number | null) {
        this.valueFieldChanged = 'L';
        this._value.low =
            this.settingPanelType === SettingPanelType.Percentage
                ? (newValue ?? 0) / 100
                : (newValue ?? 0);

        this.#emitValueChange();
    }

    onHighValueChange(newValue: number | null) {
        this.valueFieldChanged = 'H';
        this._value.high =
            this.settingPanelType === SettingPanelType.Percentage
                ? (newValue ?? 0) / 100
                : (newValue ?? 0);

        this.#emitValueChange();
    }

    onEnabledValueChange(newValue: boolean | null) {
        this._value.enabled = newValue ?? false;
        this.#emitValueChange();
    }

    onTargetValueTimeChange(value: {
        type: 'D' | 'H' | 'M' | 'S';
        newValue: number | null;
    }) {
        this.timeFieldChanged = 'T' + value.type;
        switch (value.type) {
            case 'D':
                this._targetTime.days = value.newValue ?? 0;
                break;

            case 'H':
                this._targetTime.hours = value.newValue ?? 0;
                break;

            case 'M':
                this._targetTime.minutes = value.newValue ?? 0;
                break;

            case 'S':
                this._targetTime.seconds = value.newValue ?? 0;
                break;
        }

        (this._value as ITargetThreshold).target = durationToSeconds(
            this._targetTime,
        );
        this.#emitValueChange();
    }

    onLowValueTimeChange(value: {
        type: 'D' | 'H' | 'M' | 'S';
        newValue: number | null;
    }) {
        this.timeFieldChanged = 'L' + value.type;
        switch (value.type) {
            case 'D':
                this._lowTime.days = value.newValue ?? 0;
                break;

            case 'H':
                this._lowTime.hours = value.newValue ?? 0;
                break;

            case 'M':
                this._lowTime.minutes = value.newValue ?? 0;
                break;

            case 'S':
                this._lowTime.seconds = value.newValue ?? 0;
                break;
        }

        this._value.low = durationToSeconds(this._lowTime);
        this.#emitValueChange();
    }

    onHighValueTimeChange(value: {
        type: 'D' | 'H' | 'M' | 'S';
        newValue: number | null;
    }) {
        this.timeFieldChanged = 'H' + value.type;
        switch (value.type) {
            case 'D':
                this._highTime.days = value.newValue ?? 0;
                break;

            case 'H':
                this._highTime.hours = value.newValue ?? 0;
                break;

            case 'M':
                this._highTime.minutes = value.newValue ?? 0;
                break;

            case 'S':
                this._highTime.seconds = value.newValue ?? 0;
                break;
        }

        this._value.high = durationToSeconds(this._highTime);
        this.#emitValueChange();
    }

    errorMessagelowHigh = 'Low cannot exceed High value';
    errorMessageTarget = 'Low/High cannot exceed Target value';
    errorMessage = '';

    get isValid(): boolean {
        if (!this._value.enabled) return true;
        // set error message type here
        if (this.isTargetThreshold) {
            const target = (this._value as ITargetThreshold).target ?? 0;
            const thresholdValid =
                target >= this._value.high && target >= this._value.low;
            const lowHighValid = this._value.high >= this._value.low;
            const allIsValid =
                target >= this._value.high &&
                this._value.high >= this._value.low;
            if (!thresholdValid) {
                this.errorMessage = this.errorMessageTarget;
            } else if (!lowHighValid) {
                this.errorMessage = this.errorMessagelowHigh;
            }
            return allIsValid;
        } else {
            const lowHighValid = this._value.high >= this._value.low;
            if (!lowHighValid) {
                this.errorMessage = this.errorMessagelowHigh;
            }
            return lowHighValid;
        }
    }

    get isTargetThreshold(): boolean {
        return (this.value as ITargetThreshold).target !== undefined;
    }

    get teamValue(): ITargetThreshold {
        return this.value as ITargetThreshold;
    }

    get panelClass(): string {
        return (this.settingPanelType === SettingPanelType.Numeric ||
            this.settingPanelType === SettingPanelType.Percentage) &&
            this.orientation === 'horizontal'
            ? 'd-flex flex-row justify-content-between'
            : '';
    }

    #emitValueChange() {
        this.valueChange.emit({
            value: this._value,
            isValid: this.isValid,
        });
    }
}
